import { type ReactNode, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, Menu, Shield, Sparkles, X } from "lucide-react";

import { supabase } from "@/lib/supabaseClient";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";

type StatusTone = "success" | "warning" | "critical" | "neutral";

export interface AdminHeaderProps {
  title?: string;
  description?: string;
  breadcrumbs?: Array<{ label: string; href?: string }>;
  actions?: ReactNode;
  status?: {
    tone?: StatusTone;
    label: string;
  };
  onToggleSidebar?: () => void;
  isSidebarOpen?: boolean;
}

const statusStyles: Record<StatusTone, string> = {
  success: "bg-emerald-50 text-emerald-700 border-emerald-200",
  warning: "bg-amber-50 text-amber-700 border-amber-200",
  critical: "bg-rose-50 text-rose-700 border-rose-200",
  neutral: "bg-slate-50 text-slate-600 border-slate-200",
};

export function AdminHeader({
  title = "Central administrativa Toc Toc",
  description,
  breadcrumbs,
  actions,
  status,
  onToggleSidebar,
  isSidebarOpen,
}: AdminHeaderProps) {
  const navigate = useNavigate();
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [loadingLogout, setLoadingLogout] = useState(false);

  useEffect(() => {
    let mounted = true;

    const loadSession = async () => {
      try {
        const { data } = await supabase.auth.getUser();
        if (mounted) {
          setUserEmail(data.user?.email ?? null);
        }
      } catch (error) {
        console.error("Não foi possível obter o usuário atual", error);
      }
    };

    void loadSession();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setUserEmail(session?.user?.email ?? null);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const initials = useMemo(() => {
    if (!userEmail) return "TT";
    return userEmail
      .split("@")[0]
      .split(/[.\-_]/)
      .filter(Boolean)
      .map(part => part.charAt(0).toUpperCase())
      .join("")
      .slice(0, 2);
  }, [userEmail]);

  const resolvedBreadcrumbs = useMemo(() => {
    if (breadcrumbs?.length) {
      return breadcrumbs;
    }

    return [
      { label: "Admin", href: "/admin" },
      { label: title },
    ];
  }, [breadcrumbs, title]);

  async function handleLogout() {
    try {
      setLoadingLogout(true);
      await supabase.auth.signOut();
      navigate("/admin-login");
    } catch (error) {
      console.error("Erro ao sair do painel", error);
    } finally {
      setLoadingLogout(false);
    }
  }

  return (
    <header className="border-b border-slate-200 bg-white/90 backdrop-blur">
      <div className="w-full px-4 py-6 sm:px-6 lg:px-10">
        <div className="flex w-full max-w-7xl flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
          <div className="space-y-4">
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-3">
                {onToggleSidebar && (
                <Button
                  variant="outline"
                  size="icon"
                  className="border-slate-200 text-slate-600 hover:border-[#FF5605] hover:text-[#FF5605] md:hidden"
                  onClick={onToggleSidebar}
                  aria-label={isSidebarOpen ? "Fechar menu de navegação" : "Abrir menu de navegação"}
                >
                  {isSidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </Button>
              )}
              <div className="flex flex-wrap items-center gap-3 text-xs uppercase tracking-[0.3em] text-slate-500">
                <span className="inline-flex items-center gap-2 rounded-full border border-[#3332FC]/20 bg-[#3332FC]/10 px-3 py-1 text-[0.65rem] font-semibold text-[#3332FC]">
                  <Shield className="h-3.5 w-3.5" /> Área Administrativa
                </span>
                <Sparkles className="h-3.5 w-3.5 text-[#FF5605]" />
                <span className="font-semibold text-slate-600">Experiência Toc Toc</span>
              </div>
            </div>

            <Breadcrumb>
              <BreadcrumbList className="flex flex-wrap items-center gap-2 text-sm text-slate-500">
                {resolvedBreadcrumbs.map((item, index) => (
                  <div className="flex items-center gap-2" key={`${item.label}-${index}`}>
                    <BreadcrumbItem>
                      {item.href ? (
                        <BreadcrumbLink asChild>
                          <Link className="text-slate-600 hover:text-[#FF5605]" to={item.href}>
                            {item.label}
                          </Link>
                        </BreadcrumbLink>
                      ) : (
                        <BreadcrumbLink className="text-slate-500">{item.label}</BreadcrumbLink>
                      )}
                    </BreadcrumbItem>
                    {index < resolvedBreadcrumbs.length - 1 && <BreadcrumbSeparator className="text-slate-300" />}
                  </div>
                ))}
              </BreadcrumbList>
            </Breadcrumb>
          </div>

          <div className="space-y-2">
            <h1 className="text-3xl font-semibold text-slate-900 sm:text-4xl text-pretty [overflow-wrap:anywhere]">{title}</h1>
            {description && (
              <p className="max-w-2xl text-sm text-slate-600 text-pretty [overflow-wrap:anywhere]">{description}</p>
            )}
            {status && (
              <Badge
                variant="outline"
                className={cn(
                  "flex w-fit items-center gap-2 border text-xs font-medium",
                  statusStyles[status.tone ?? "neutral"]
                )}
              >
                <span className="block h-2 w-2 rounded-full bg-current" aria-hidden="true" />
                {status.label}
              </Badge>
            )}
          </div>
        </div>

        <div className="flex w-full flex-col gap-4 sm:flex-row sm:items-center sm:justify-end lg:w-auto">
          {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}

          <div className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
            <Avatar className="h-10 w-10 border border-slate-200 bg-slate-50">
              <AvatarFallback className="bg-[#3332FC] text-sm font-semibold text-white">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-[160px]">
              <p className="text-sm font-medium text-slate-900">{userEmail ?? "Acesso convidado"}</p>
              <p className="text-xs text-slate-500">Administração Toc Toc</p>
            </div>
            <Separator orientation="vertical" className="h-8 bg-slate-200" />
            <Button
              variant="outline"
              size="sm"
              className="border-[#FF5605]/30 text-[#FF5605] hover:bg-[#FF5605]/10"
              onClick={handleLogout}
              disabled={loadingLogout}
            >
              <LogOut className="mr-2 h-4 w-4" />
              {loadingLogout ? "Saindo..." : "Sair"}
            </Button>
          </div>
        </div>
        </div>
      </div>
    </header>
  );
}
